import { styled, keyframes } from "@pigment-css/react";
import { useTranslations } from "@acto/web-common/locales";
import { ChevronRight } from "@acto/web-common/components";
import type ActivityMetrics from "@acto/taskpane/core/domain/ActivityMetrics";
import type { ActivityType } from "../EmployeeSidePanel";

interface ActivitiesSectionProps {
  activityMetrics: ActivityMetrics | null;
  onActivityClick: (activity: ActivityType) => void;
  activeActivity?: ActivityType | null;
  isLoading?: boolean;
}

const ActivityGroup = styled("section")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.managementView.spacing[12],
}));

const ActivityHeading = styled("h3")(({ theme }) => ({
  fontFamily: theme.managementView.font.familySecondary,
  fontSize: theme.managementView.font.size[16],
  fontWeight: theme.managementView.font.weight[400],
  lineHeight: theme.font.lineHeight[10],
  letterSpacing: theme.managementView.font.letterSpacing.tight,
  color: theme.managementView.colors.neutral[100],
  margin: 0,
}));

const ActivityList = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.managementView.spacing[8],
}));

const ActivityItem = styled("button")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.managementView.spacing[12],
  marginLeft: `-${theme.managementView.spacing[12]}`,
  width: `calc(100% + ${theme.managementView.spacing[12]})`,
  backgroundColor: theme.managementView.colors.neutral[0],
  border: "none",
  borderRadius: theme.managementView.radius[16],
  cursor: "pointer",
  transition: "background-color 0.2s",

  "&:hover": {
    backgroundColor: theme.managementView.colors.neutral[4],
    "& span": {
      border: `1px solid ${theme.managementView.colors.neutral[8]}`,
    },
  },

  "&[data-active='true']": {
    backgroundColor: theme.managementView.colors.neutral[4],
  },

  "&[data-disabled='true']": {
    cursor: "default",
    "&:hover": {
      backgroundColor: theme.managementView.colors.neutral[0],
      "& span": {
        border: "1px solid transparent",
      },
    },
  },

  "& svg": {
    width: "10px",
    height: "10px",
    color: theme.managementView.colors.neutral[64],
  },
}));

const ActivityContent = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.managementView.spacing[16],
}));

const ActivityCount = styled("span")(({ theme }) => ({
  fontFamily: theme.managementView.font.familySecondary,
  fontSize: theme.managementView.font.size[14],
  fontWeight: theme.managementView.font.weight[500],
  color: theme.managementView.colors.neutral[56],
  borderRadius: theme.managementView.radius[32],
  letterSpacing: theme.managementView.font.letterSpacing.tight,
  backgroundColor: theme.managementView.colors.neutral[4],
  padding: `0 ${theme.managementView.spacing[12]}`,
  border: `1px solid transparent`,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  boxSizing: "content-box",
  minWidth: "3ch",
  height: "26px",
  flexShrink: 0,
  fontVariantNumeric: "tabular-nums",
}));

const ActivityLabel = styled("p")(({ theme }) => ({
  fontFamily: theme.managementView.font.familySecondary,
  fontSize: theme.managementView.font.size[15],
  fontWeight: theme.managementView.font.weight[440],
  lineHeight: theme.font.lineHeight[20],
  color: theme.managementView.colors.neutral[90],
  letterSpacing: theme.managementView.font.letterSpacing.tight,
}));

const shimmer = keyframes({
  "0%": { backgroundPosition: "-200px 0" },
  "100%": { backgroundPosition: "200px 0" },
});

const SkeletonElement = styled("div")(({ theme }) => ({
  borderRadius: theme.managementView.radius[10],
  background: `linear-gradient(90deg, ${theme.colors.neutral[200]} 0%, ${theme.colors.neutral[100]} 50%, ${theme.colors.neutral[200]} 100%)`,
  backgroundSize: "400px 100%",
  animation: `${shimmer} 1.5s ease-in-out infinite`,
}));

const SkeletonCount = styled(SkeletonElement)(({ theme }) => ({
  fontFamily: theme.managementView.font.familySecondary,
  fontSize: theme.managementView.font.size[14],
  fontWeight: theme.managementView.font.weight[500],
  fontVariantNumeric: "tabular-nums",
  width: `calc(3ch + 2 * ${theme.managementView.spacing[12]} + 2px)`,
  height: "28px",
  borderRadius: theme.managementView.radius[32],
  flexShrink: 0,
}));

const SkeletonLabel = styled(SkeletonElement)({
  height: "15px",
  width: "140px",
});

export const ActivitiesSection = ({
  activityMetrics,
  onActivityClick,
  activeActivity,
  isLoading,
}: ActivitiesSectionProps) => {
  const t = useTranslations();

  const dismissalsCount = activityMetrics?.dismissalsCount ?? 0;
  const openTodosCount = activityMetrics?.openTodosCount ?? 0;
  const markedDoneCount = activityMetrics?.markedDoneCount ?? 0;
  const appointmentStates = activityMetrics?.appointmentStates;

  const renderRow = (activity: ActivityType, count: number, label: string) => (
    <ActivityItem
      key={activity}
      onClick={count > 0 ? () => onActivityClick(activity) : undefined}
      data-active={activeActivity === activity}
      data-disabled={count === 0}
    >
      <ActivityContent>
        <ActivityCount>{count}</ActivityCount>
        <ActivityLabel>{label}</ActivityLabel>
      </ActivityContent>
      {count > 0 && <ChevronRight />}
    </ActivityItem>
  );

  if (isLoading) {
    return (
      <ActivityGroup>
        <ActivityHeading>{t("managementView.employee.activities")}</ActivityHeading>
        <ActivityList>
          {Array.from({ length: 4 }).map((_, index) => (
            <ActivityItem key={index} as="div" data-disabled="true">
              <ActivityContent>
                <SkeletonCount />
                <SkeletonLabel />
              </ActivityContent>
            </ActivityItem>
          ))}
        </ActivityList>
      </ActivityGroup>
    );
  }

  return (
    <>
      <ActivityGroup>
        <ActivityHeading>{t("managementView.employee.appointments")}</ActivityHeading>
        <ActivityList>
          {renderRow(
            "plannedAppointments",
            appointmentStates?.planned ?? 0,
            t("managementView.employee.plannedAppointments"),
          )}
          {renderRow(
            "noReportAppointments",
            appointmentStates?.noReport ?? 0,
            t("managementView.employee.noReportAppointments"),
          )}
          {renderRow(
            "documentedAppointments",
            appointmentStates?.documented ?? 0,
            t("managementView.employee.documentedAppointments"),
          )}
          {renderRow(
            "didNotTakePlaceAppointments",
            appointmentStates?.didNotTakePlace ?? 0,
            t("managementView.employee.didNotTakePlaceAppointments"),
          )}
        </ActivityList>
      </ActivityGroup>
      <ActivityGroup>
        <ActivityHeading>{t("managementView.employee.activities")}</ActivityHeading>
        <ActivityList>
          {renderRow("openTodos", openTodosCount, t("managementView.employee.openTodos"))}
          {renderRow("markedDone", markedDoneCount, t("managementView.employee.markedDone"))}
          {renderRow("ignoredSignals", dismissalsCount, t("managementView.employee.notInterested"))}
        </ActivityList>
      </ActivityGroup>
    </>
  );
};
